// Tree-walk helpers shared by every per-dialect tree-sitter fork (oracle /
// mssql / postgres / …). Extracts RecoveryInfo (ERROR + MISSING ranges). The
// walker is dialect-agnostic: it consumes the tree-sitter Node contract.
// Extracted by Slice 3.5 (n=3 forks) after the per-dialect modules proved to
// be byte-identical.

import type { Node } from 'web-tree-sitter';

import type { RecoveryInfo, SqlRange } from '../../model';
import { rangeFromOffsets, type LineIndex } from '../../document/lineIndex';

interface TreeWalkResult {
  errorRanges: SqlRange[];
  missingRanges: SqlRange[];
}

function walk(root: Node, idx: LineIndex): TreeWalkResult {
  const errorRanges: SqlRange[] = [];
  const missingRanges: SqlRange[] = [];
  if (!root.hasError && !root.isError && !root.isMissing) {
    return { errorRanges, missingRanges };
  }

  const stack: Node[] = [root];
  while (stack.length > 0) {
    const node = stack.pop()!;
    if (node.isMissing) {
      missingRanges.push(rangeFromOffsets(idx, node.startIndex, node.endIndex));
      continue;
    }
    if (node.isError) {
      errorRanges.push(rangeFromOffsets(idx, node.startIndex, node.endIndex));
    }
    if (!node.hasError && !node.isError) continue;
    for (const child of node.children) stack.push(child);
  }
  return { errorRanges, missingRanges };
}

export function buildRecoveryInfoFromTree(root: Node, idx: LineIndex): RecoveryInfo {
  const { errorRanges, missingRanges } = walk(root, idx);
  const truncated = false;
  const confidence: RecoveryInfo['confidence'] =
    errorRanges.length === 0 && missingRanges.length === 0 ? 'high' : 'low';
  return { errorRanges, missingRanges, truncated, confidence };
}
